import { pascalCase } from 'change-case'
import { FieldDefinition } from './client'

/*
 * A definer always returns one field only for a resource.  Some resources may not have any field that is an obvious definer field.
 * In that case, we would want to combine multiple fields to describe that record which can be controlled better in the getSearchFieldsForResource function.
 * Outside of this file, there is also the option to update the Option component to change the UI.
 */
export const getDefinerForResource = (fieldDefinition: FieldDefinition) => {
  const { fields, name: pascalCaseName } = fieldDefinition
  const hasNameField = fields && fields?.some((field) => field.name === 'Name')

  switch (pascalCase(pascalCaseName)) {
    case 'Availabilities':
      return 'Type'
    case 'Jobs':
      return 'Description'
    case 'Contacts':
      return 'FullName'
    case 'RecordFilters':
      return 'Description'
    default:
      // The first field name in the array is usually UID
      return hasNameField ? 'Name' : 'UID'
  }
}

/*
 * This is used to construct multiple definers for junction fields based on the definer of that resource.
 * For example, AccountTags will get the definer of Account and Tags.  Note that this pattern will not match all junction fields
 * so where we want more flexibility and to specify which exact fields, we can use the getSearchFieldsForResource function.
 */
export const getResourcesFromJunctionFields = (resourceName: string) => {
  switch (pascalCase(resourceName)) {
    case 'AccountResourceScores':
      return ['Accounts', 'Resources']
    case 'AccountTags':
      return ['Accounts', 'Tags']
    case 'ActivityResources':
      return ['Activities', 'Resources']
    case 'AvailabilityPatternResources':
      return ['AvailabilityPatterns', 'Resources']
    case 'AvailabilityTemplateEntries':
      return ['AvailabilityTemplates']
    case 'AvailabilityTemplateResources':
      return ['AvailabilityTemplates', 'Resources']
    case 'ClientAvailabilities':
      return ['Contacts', 'Accounts']
    case 'ContactTags':
      return ['Contacts', 'Tags']
    case 'HolidayRegions':
      return ['Holidays', 'Regions']
    case 'JobOffers':
      return ['Jobs']
    case 'JobTags':
      return ['Jobs', 'Tags']
    case 'LocationResourceScores':
      return ['Locations', 'Resources']
    case 'ResourceOverrides':
      return ['Resources']
    case 'ResourceRegions':
      return ['Resources', 'Regions']
    case 'ResourceRequirementTags':
      return ['ResourceRequirements', 'Tags']
    case 'ResourceShifts':
      return ['Resources', 'Shifts']
    case 'ResourceTags':
      return ['Resources', 'Tags']
    case 'ShiftTags':
      return ['Shifts', 'Tags']
    case 'TemplatedActivities':
      return ['ScheduleTemplates']
    case 'UserRegions':
      return ['Users', 'Regions']
    default:
      return null
  }
}

/*
 * This is used to set the default search fields for a resource and then to display in the dropdown in the format "field1 - field2".
 */
export const getSearchFieldsForResource = (
  fieldDefinition: FieldDefinition,
) => {
  const { name: pascalCaseName } = fieldDefinition

  const definer = getDefinerForResource(fieldDefinition)

  switch (pascalCase(pascalCaseName)) {
    case 'Activities':
      return [definer, 'Type']
    case 'Availabilities':
      return [definer, 'Resource.Name']
    case 'JobDependencies':
      return ['FromJob.Name', 'ToJob.Name']
    case 'JobProducts':
      return [definer, 'Product.Name']
    case 'JobTasks':
      return [definer, 'Job.Description']
    case 'ResourceGroupMembers':
      // ResourceGroup is not a separate resource (it is the same as resource) so cannot go into junction fields
      return ['Resource.Name', 'ResourceGroup.Name']
    case 'ResourceJobOffers':
      return ['Resource.Name', 'JobOffer.Job.Description']
    case 'ResourceOverrideRegions':
      return ['ResourceOverride.Resource.Name', 'Region.Name']
    default:
      return [definer]
  }
}
